import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { TodoItem, TodosResponse } from '@indipia/shared'
import { Badge, Button, SignalDot, TextLink } from '@indipia/ui-core'
import { Z_CONTEXT_MENU } from '../constants/zIndex'
import { hasSignalAction } from '../constants/signalView'

// [오늘 할일 N] 패널 — 상단바 버튼에서 연다 (940 시트4 ④ 'C-1 상단바 [오늘 할일] · A홈 오늘 할일').
// 머리 = '오늘 할일 · 현장명 · N건', 하단 링크 = '전체 할일 보기 ›'(행선지는 호스트 주입 /todo).
// '지금 처리할 일'(빨강) · '오늘 일정'(노랑) 두 칸, 줄 = 할일 제목 · 공종 · 사유 + 버튼 1개.
// 칸 · 순서 · 제목 · 사유 · 버튼 · 이동 주소 · NEW 는 서버(TODO-01)가 정해 내려준 그대로 그린다. 할일 코드는 보이지 않는다.
// 0건이면 이 패널을 여는 버튼 자체가 보이지 않으므로(호출부) 빈 상태는 만들지 않는다.
// 서버 신호 오류는 명시적으로 표시한다(폴백 없음).

const PANEL_WIDTH = 400

interface Props {
  anchor: { x: number; y: number }
  data: TodosResponse | undefined
  isLoading: boolean
  isError: boolean
  onRetry: () => void
  onClose: () => void
  /** 현장 이름을 줄에 붙인다(여러 현장이 섞이는 A홈) */
  showSite?: boolean
  /** 머리에 붙일 현장명(한 현장 화면) — 여러 현장이 섞이면 주지 않는다 */
  siteName?: string
  /** 이 줄의 버튼 주소로 갈 수 있는지 — 간트 주소는 화면이 열고, 그 밖은 호스트 이동이 있어야 한다(없으면 버튼 숨김) */
  canAct: (item: TodoItem) => boolean
  /** 처리 버튼 — 서버가 내려준 주소(webPath)·호출 주소(actionUrl)로 처리한다 */
  onAct: (item: TodoItem) => void
  /** 셸 나의 할일 화면으로 이동(호스트 주입). 없으면 링크를 숨긴다 */
  onOpenTodoPage?: () => void
}

function TodoRow({ item, showSite, canAct, onAct }: {
  item: TodoItem
  showSite?: boolean
  canAct: (item: TodoItem) => boolean
  onAct: (item: TodoItem) => void
}) {
  const showAct = hasSignalAction(item) && canAct(item)
  return (
    <div style={{ padding: 'var(--spacing-03) 0', borderTop: 'var(--border-width-hairline) solid var(--shell-border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <SignalDot color={item.level === 'r' ? 'RED' : 'YELLOW'} label={item.level === 'r' ? '지금 처리할 일' : '오늘 일정'} />
        <b style={{ fontSize: 12, color: 'var(--shell-on-surface)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {item.todoName ?? item.workTypeName ?? ''}
        </b>
        {item.isNew && <Badge status="accent">NEW</Badge>}
      </div>
      <div style={{ fontSize: 12, color: 'var(--shell-on-surface-2)', marginTop: 3, paddingLeft: 16, lineHeight: 1.5 }}>
        {[showSite ? item.siteName : null, item.workTypeName, item.todoReasonText].filter(Boolean).join(' · ')}
      </div>
      {showAct && (
        <div style={{ marginTop: 5, paddingLeft: 16 }}>
          <Button variant="secondary" size="sm" onClick={() => onAct(item)}>{item.actionName}</Button>
        </div>
      )}
    </div>
  )
}

export function SignalTodoPanel({
  anchor, data, isLoading, isError, onRetry, onClose, showSite, siteName, canAct, onAct, onOpenTodoPage,
}: Props) {
  const boxRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ left: number; top: number }>({ left: anchor.x, top: anchor.y })

  useLayoutEffect(() => {
    const el = boxRef.current
    const h = el?.offsetHeight ?? 0
    const left = Math.max(8, Math.min(anchor.x, window.innerWidth - PANEL_WIDTH - 8))
    const top = anchor.y + h + 8 > window.innerHeight ? Math.max(8, window.innerHeight - h - 8) : anchor.y
    setPos({ left, top })
  }, [anchor.x, anchor.y, data, isError, isLoading])

  useEffect(() => {
    const onDown = (e: MouseEvent) => { if (!boxRef.current?.contains(e.target as Node)) onClose() }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey) }
  }, [onClose])

  const items = data?.items ?? []
  const red = items.filter(i => i.level === 'r')
  const yellow = items.filter(i => i.level === 'y')
  const section = (title: string, list: TodoItem[]) => list.length === 0 ? null : (
    <div style={{ marginBottom: 6 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--shell-on-surface-2)', marginBottom: 2 }}>{title} {list.length}</div>
      {list.map(i => (
        <TodoRow
          key={`${i.taskId}-${i.signalTypeCode}-${i.todoCode}`}
          item={i}
          showSite={showSite}
          canAct={canAct}
          onAct={onAct}
        />
      ))}
    </div>
  )

  return (
    <div
      ref={boxRef}
      role="dialog"
      aria-label="오늘 할일"
      onMouseDown={e => e.stopPropagation()}
      onClick={e => e.stopPropagation()}
      style={{
        position: 'fixed', left: pos.left, top: pos.top, width: PANEL_WIDTH, maxHeight: 'min(70vh, 560px)', overflowY: 'auto', zIndex: Z_CONTEXT_MENU,
        background: 'var(--shell-surface)', border: 'var(--border-width-hairline) solid var(--shell-border)',
        borderRadius: 12, boxShadow: 'var(--elevation-2)', padding: 'var(--spacing-03) var(--spacing-04)', cursor: 'default',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <b style={{ flex: 1, fontSize: 13, color: 'var(--shell-on-surface)' }}>
          {['오늘 할일', siteName, data ? `${data.summary.pendingCount + data.summary.todayCount}건` : null].filter(Boolean).join(' · ')}
        </b>
        <Button variant="ghost" size="sm" aria-label="닫기" onClick={onClose}>×</Button>
      </div>

      {isError ? (
        <div role="alert" style={{ padding: 'var(--spacing-03) 0' }}>
          <div style={{ fontSize: 12, color: 'var(--status-danger-600)', marginBottom: 6 }}>신호 정보를 불러오지 못했어요</div>
          <Button variant="secondary" size="sm" onClick={onRetry}>다시 시도</Button>
        </div>
      ) : isLoading && !data ? (
        <div aria-busy="true" style={{ fontSize: 12, color: 'var(--shell-on-surface-2)', padding: 'var(--spacing-03) 0' }}>불러오는 중이에요</div>
      ) : (
        <>
          {section('지금 처리할 일', red)}
          {section('오늘 일정', yellow)}
        </>
      )}

      {onOpenTodoPage && (
        <div style={{ borderTop: 'var(--border-width-hairline) solid var(--shell-border)', paddingTop: 8, textAlign: 'right' }}>
          <TextLink onClick={onOpenTodoPage}>전체 할일 보기 ›</TextLink>
        </div>
      )}
    </div>
  )
}
